/**
 * /help-me-connect-ecowitt — One-screen, mobile-first companion to the
 * full /ecowitt-to-rachio page.
 *
 * Why a separate URL:
 *   - The full page is 600+ lines of credibility content built for Google
 *     and skeptical readers. It's perfect when someone is actively
 *     evaluating you.
 *   - The mini page is the link you drop in a Facebook comment, a Reddit
 *     reply, or a DM. It loads on phones in under a second, gives the
 *     "yes — there IS a solution" answer in 3 sentences, and routes the
 *     curious to the full page.
 *
 * Tone: like a friend texting you the fix. Not a sales page.
 *
 * SEO: still emits proper canonical/OG/Twitter via <SEO />. Lower priority
 * than /ecowitt-to-rachio in sitemap (0.7 vs 0.9) so Google understands
 * which page is the canonical hub.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { Button } from '../components/ui/button';
import { ShareButtons } from '../components/ShareButtons';
import {
  CheckCircle2, ArrowRight, Clock, Wifi, Cpu,
} from 'lucide-react';

const URL = 'https://getturfpulse.com/help-me-connect-ecowitt';

export default function HelpMeConnectEcowittPage() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-amber-50 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900"
      data-testid="help-me-connect-ecowitt-page"
    >
      <SEO
        title="Yes — you can connect Ecowitt to Rachio. Here's the 5-min fix."
        description="Rachio still has no native Ecowitt support. The Rachio Community thread has been silent since 2024. We built the bridge. 5 minutes, no Raspberry Pi."
        canonical={URL}
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Help me connect Ecowitt to Rachio', path: '/help-me-connect-ecowitt' },
        ]}
      />

      <div className="mx-auto max-w-2xl px-5 py-12 sm:py-16">
        {/* Hero answer — first thing the eye lands on */}
        <div
          className="rounded-2xl border-2 border-emerald-400 dark:border-emerald-600 bg-white dark:bg-zinc-900 p-6 sm:p-8 shadow-lg shadow-emerald-500/10"
          data-testid="help-hero"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="w-3.5 h-3.5" /> Yes, there's a solution
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight text-zinc-900 dark:text-zinc-50 leading-tight">
            You're not crazy.<br />
            <span className="text-emerald-700 dark:text-emerald-400">
              Ecowitt → Rachio isn't native.
            </span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-zinc-700 dark:text-zinc-200 leading-relaxed">
            The Rachio Community thread on this has been silent since 2024.
            Neither company has shipped a bridge. So we built one.
          </p>

          <div
            className="mt-5 grid grid-cols-3 gap-3 text-center"
            data-testid="help-stats"
          >
            <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/60 p-3">
              <Clock className="mx-auto w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <div className="mt-1 text-lg font-black text-zinc-900 dark:text-zinc-50">5 min</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">setup</div>
            </div>
            <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/60 p-3">
              <Wifi className="mx-auto w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <div className="mt-1 text-lg font-black text-zinc-900 dark:text-zinc-50">15 min</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">poll rate</div>
            </div>
            <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/60 p-3">
              <Cpu className="mx-auto w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <div className="mt-1 text-lg font-black text-zinc-900 dark:text-zinc-50">$0</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">to start</div>
            </div>
          </div>

          <Link to="/?auth=1" className="block">
            <Button
              className="mt-6 w-full bg-emerald-700 hover:bg-emerald-800 text-white text-base font-bold py-6"
              data-testid="help-cta-primary"
            >
              Make a free account <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <p className="mt-2 text-center text-xs text-zinc-500 dark:text-zinc-400">
            No card. No Pi. No YAML.
          </p>
        </div>

        {/* Three-step "what happens next" */}
        <div className="mt-8" data-testid="help-steps">
          <h2 className="text-sm uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400">
            How it works
          </h2>
          <ol className="mt-3 space-y-3">
            <li className="flex gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white flex items-center justify-center text-sm font-black">
                1
              </div>
              <div className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">
                <strong className="text-zinc-900 dark:text-zinc-50">Paste your Rachio + Ecowitt API keys.</strong>
                {' '}Both are free, both take 30 seconds to grab from each app.
              </div>
            </li>
            <li className="flex gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white flex items-center justify-center text-sm font-black">
                2
              </div>
              <div className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">
                <strong className="text-zinc-900 dark:text-zinc-50">Pair each Rachio zone to a probe.</strong>
                {' '}You can use one probe per zone, or just put probes in your driest + wettest spots.
              </div>
            </li>
            <li className="flex gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-700 text-white flex items-center justify-center text-sm font-black">
                3
              </div>
              <div className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">
                <strong className="text-zinc-900 dark:text-zinc-50">Done.</strong>
                {' '}Your Rachio keeps its app, schedules, and zones. We just sit on top and say "yes, fire" or "no, the soil is fine" before each run.
              </div>
            </li>
          </ol>
        </div>

        {/* Receipts — links to the credibility content */}
        <div
          className="mt-8 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5"
          data-testid="help-receipts"
        >
          <h2 className="text-sm uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400">
            Receipts (for the skeptical)
          </h2>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link
                to="/ecowitt-to-rachio"
                className="text-emerald-700 dark:text-emerald-400 underline font-semibold"
                data-testid="help-link-full-page"
              >
                Full honest comparison: 4 paths, cost table, why this exists →
              </Link>
            </li>
            <li>
              <Link
                to="/methodology/probe-driven-irrigation"
                className="text-emerald-700 dark:text-emerald-400 underline font-semibold"
                data-testid="help-link-methodology"
              >
                The math we use (TAMU, Smith-Kerns, FAO-56) →
              </Link>
            </li>
            <li>
              <Link
                to="/credits"
                className="text-emerald-700 dark:text-emerald-400 underline font-semibold"
                data-testid="help-link-credits"
              >
                Academic citations & data sources →
              </Link>
            </li>
            <li>
              <Link
                to="/getting-started"
                className="text-emerald-700 dark:text-emerald-400 underline font-semibold"
                data-testid="help-link-getting-started"
              >
                Step-by-step setup walkthrough →
              </Link>
            </li>
          </ul>
        </div>

        {/* Share row — every visitor is also a potential distributor */}
        <div
          className="mt-8 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/30 p-5"
          data-testid="help-share-block"
        >
          <h2 className="text-sm font-bold text-emerald-900 dark:text-emerald-200">
            Know someone else stuck on this?
          </h2>
          <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-300/80">
            The Rachio Community thread on this has been quiet for 2 years.
            Most homeowners don't know there's an answer now.
          </p>
          <div className="mt-3">
            <ShareButtons
              url={URL}
              title="Yes — you can connect Ecowitt to Rachio"
              message="Rachio still has no native Ecowitt support. The Rachio Community thread has been silent since 2024. TurfPulse is the bridge — 5 min, no Raspberry Pi."
              testIdPrefix="help-share"
            />
          </div>
        </div>

        {/* Tertiary close */}
        <p className="mt-10 text-center text-xs text-zinc-500 dark:text-zinc-400">
          Built by lawn enthusiasts who got tired of waiting. Free forever
          tier. Inner Circle founding seats are <strong>$4.99/mo locked for life</strong>.
        </p>
      </div>
    </div>
  );
}
